import { ImageModel } from '../General';
import { SelectOption } from '../General/Select';

export interface JobChainData {
  jobChainId: number;
  jobChainType: string;
  jobChainTypeDescription: string;
  customerId: number;
  customerName: string;
  subCustomerId?: number;
  subCustomerName?: string;
  stockId: number;
  vins?: string[];
  locationId?: number;
  location?: string;
  statusId: number;
  status: string;
  jobChainTemplateId: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  completedAt?: string;
  completedBy?: string;
  jobSource: string;
  notes: string;
  extraData: string;
  tags: JobTag[];
}

export interface JobChainFilterData {
  types: SelectOption<string>[];
  customers: SelectOption<number>[];
  subCustomers: SelectOption<number>[];
  locations: SelectOption<number>[];
}

export interface JobListData {
  jobId: number;
  jobChainId: number;
  jobChainType: string;
  jobChainTypeDescription: string;
  jobType: string;
  jobTypeDescription: string;
  vin?: string;
  locationId?: number;
  location?: string;
  jobStatusId: number;
  jobStatus: string;
  customerId: number;
  customerName: string;
  subCustomerId?: number;
  subCustomerName?: string;
  notes?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  completedAt?: string;
  completedBy?: string;
}

export interface BatteryCheckExportRow {
  jobId: number;
  jobChainId: number;
  vin?: string;
  site?: string;
  customerName?: string;
  subCustomerName?: string;
  jobStatus?: string;
  engineType?: string;
  transportMode?: string;
  battery12VHealth?: string;
  battery12VHealthComment?: string;
  battery12VChargeRequired?: string;
  battery12VLevel?: number;
  battery12VLevelUnit?: string;
  batteryHVHealth?: string;
  batteryHVHealthComment?: string;
  batteryHVChargeRequired?: string;
  batteryHVSOC?: number;
  batteryHVSOCUnit?: string;
  followOn12VChargeStatus?: string;
  followOnHVChargeStatus?: string;
  createdAt: string;
  completedAt?: string;
}

export enum JobTypeEnum {
  Maintenance = 'Maintenance',
  CompoundMovement = 'CompoundMovement',
  Inspection = 'Inspection',
  Refurbishment = 'Refurbishment',
  EVCharging = 'EVCharging',
  WarmUpEngine = 'WarmUpEngine',
  AddFluids = 'AddFluids',
  AddFuel = 'AddFuel',
  RecordActivity = 'RecordActivity',
  VehicleWash = 'VehicleWash',
  BatteryCheck = 'BatteryCheck',
  TyrePressure = 'TyrePressure',
  Battery12VCharge = 'Battery12VCharge',
}

export interface CreateJobModel {
  jobType: string;
  vin: string;
  jobSource: string;
  jobTags: number[];
  extraData: any;
}

export interface CreateLoadPullRequestJobModel {
  movementRef: string;
  jobSource?: string;
  jobTags?: number[];
}

export interface JobMaintenanceModel {
  data:
    | EvChargeModel
    | WarmUpModel
    | AddFluidsModel
    | AddFuelModel
    | RecordActivityModel
    | BatteryCheckModel
    | VehicleWashModel
    | TyrePressureModel
    | Battery12VChargeModel;
  images: (ImageModel & { imageType: string })[];
}

export interface JobChainDagResponse {
  chain: ChainHeader;
  template: TemplateGraph;
  jobs: JobNodeDto[]; // full-history; includes superseded attempts
  edgeStates: EdgeStateRow[]; // one row per template.edges entry
  subjects?: JobChainSubjectDto[];
  participationEdgeStates?: ParticipationEdgeStateRow[];
}

export interface JobChainSubjectDto {
  id: number;
  subjectType: string;
  subjectKey: string;
  addedAt: string;
  removedAt: string | null;
  stockId: number | null;
  vin: string | null;
}

type JobChainStatus =
  // JobStatusEnum names (JobChain.StatusId → JobStatus entity, shared with Job)
  | 'Requested'
  | 'Scheduled'
  | 'Started'
  | 'OnHold'
  | 'Completed'
  | 'CancelledByUser'
  | 'CancelledBySystem'
  | 'Failed';

interface ChainHeader {
  id: number;
  jobChainType: string; // e.g. "B07" — JobChainType.Value string
  vin?: string | null;
  customerId: number;
  customerName: string;
  subCustomerId: number | null;
  subCustomerName: string | null;
  status: JobChainStatus; // see below
  statusId: number;
  templateId: number; // FK to TemplateJobChain
  templateVersion: number; // locked at chain creation
  createdAt: string; // ISO 8601
  createdBy: string;
  updatedAt: string | null;
  completedAt: string | null;
  completedBy: string | null;
  jobSource: string;
  notes: string | null;
}

interface TemplateGraph {
  id: number; // TemplateJobChain.Id
  name: string; // JobChainType enum name
  version: number;
  nodes: TemplateNode[];
  edges: TemplateEdge[];
}

interface TemplateNode {
  id: number; // TemplateJobChainJob.Id
  jobTemplateId: number; // TemplateJobChainJob.JobTemplateId
  jobType: string; // JobTypeEnum name
  name: string; // TemplateJob.Name
  description: string | null; // TemplateJob.Description
  joinPolicy: 'AnyLive' | 'AllLive';
  isStart: boolean; // derived: no incoming edges
  isEnd: boolean; // derived: no outgoing edges
}

interface TemplateEdge {
  id: number; // TemplateJobChainEdge.Id
  fromNodeId: number; // → TemplateJobChainJob.Id
  toNodeId: number; // → TemplateJobChainJob.Id
  conditions: EdgeCondition[]; // grouped by groupId: within-group AND, across-group OR;
  // empty = always-live when predecessor Completes
}

interface EdgeCondition {
  id: number; // JobCondition.Id
  groupId: number; // JobCondition.GroupId — same id = AND partners, different id = OR alternatives
  name: string; // JobCondition.Name (ExtraData key)
  operator: ConditionOperator; // mapped from ConditionEnum
  value: string; // JobCondition.Value
}

type ConditionOperator =
  | 'Equals'
  | 'NotEquals'
  | 'GreaterThan'
  | 'GreaterOrEqual'
  | 'LessThan'
  | 'LessOrEqual'
  | 'Contains'
  | 'NotContains'
  | 'StartsWith'
  | 'EndsWith'
  | 'In'
  | 'NotIn'
  | 'IsNull'
  | 'IsNotNull';

export interface JobNodeDto {
  id: number; // Job.JobId
  templateNodeId: number; // Job.TemplateJobChainJobId
  status: JobNodeStatus;
  supersededByJobId: number | null; // null = current effective attempt
  attempt: number; // 1..N per templateNodeId
  triggeredBy: TriggerInfo; // derived, see §3.2
  vin?: string | null;
  jobChainSubjectId?: number | null;
}

export interface ParticipationEdgeStateRow {
  edgeId: number;
  targetTemplateNodeId: number;
  jobChainSubjectId: number | null;
  state: 'Waiting' | 'Live' | 'Dead';
}

export type JobNodeStatus =
  // JobStatusEnum names from GBACommonLib.Enums.JobEnums
  | 'Requested'
  | 'Scheduled'
  | 'Started'
  | 'OnHold'
  | 'Completed'
  | 'CancelledByUser'
  | 'CancelledBySystem'
  | 'Failed';
interface TriggerInfo {
  kind: 'ChainCreated' | 'EdgeFired' | 'Retry' | 'Manual';
  edgeId: number | null;
  sourceJobId: number | null;
}

interface EdgeStateRow {
  edgeId: number;
  state: 'Waiting' | 'Live' | 'Dead';
  deadReason: DeadReason | null; // non-null iff state === 'Dead'
  conditionResults: ConditionResult[] | null; // non-null iff from-Job is Completed
}

type DeadReason =
  | 'PredecessorFailed'
  | 'PredecessorCancelled'
  | 'ConditionUnmet';

interface ConditionResult {
  conditionId: number;
  groupId: number; // mirrors EdgeCondition.groupId — lets the UI reconstruct which
  // group(s) matched via groupBy(groupId).every(c => c.met)
  name: string; // mirrors EdgeCondition.name — denormalised here so the UI can render
  // per-condition detail without joining back to template.edges[]
  met: boolean;
  actualValue: string | null; // stringified (numbers/bools → string)
  expectedOperator: ConditionOperator;
  expectedValue: string;
}

export interface JobChainDetail {
  jobChainId: number;
  jobs: JobData[];
}

export interface JobData {
  jobId: number;
  jobType: JobTypeEnum;
  jobTypeDescription: string;
  jobStatusId: number;
  jobStatus: string;
  locationId?: number;
  location?: string;
  jobNotes?: string;
  orderSequence: number;
  externalRef?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  completedAt?: string;
  completedBy?: string;
  maintenance: JobMaintenanceModel;
  logs: JobLogModel[];
  items: JobItem[];
}

export interface JobItem {
  jobItemId: number;
  templateName: string;
  templateDescription: string;
  jobStatusId: number;
  jobStatus: string;
  orderSequence?: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  completedAt?: string;
  completedBy?: string;
  logs: JobLogModel[];
}

export interface JobLogModel {
  id: number;
  jobStatusId: number;
  status: string;
  performedBy: string;
  performedAt: string;
  latititude: number;
  longitude: number;
  accuracy?: number;
  elevation?: number;
  verifiedBy: string;
  verifiedAt: string;
  note?: string;
}

export interface JobStatus {
  jobStatusId: number;
  name: string;
  description: string;
}

export interface JobTag {
  id: number;
  name: string;
  deleted: boolean;
}

export interface JobTemplate {
  jobTemplateId: number;
  jobType?: string;
  jobTypeDescription?: string;
  name: string;
  description: string;
}

export interface JobItemTemplate {
  jobItemTemplateId: number;
  name: string;
  description: string;
  orderSequence: number;
}

export interface MaintenanceBaseModel {
  maintenanceType?: string;
  createdAt?: string;
  createdBy?: string;
  startedAt?: string;
  startedBy?: string;
  endedAt?: string;
  endedBy?: string;
  failedAt?: string;
  failedBy?: string;
  notes?: string;
}

export enum FluidTypeEnum {
  PowerSteeringFluid = 'PowerSteeringFluid',
  BrakeFluid = 'BrakeFluid',
  WasherFluid = 'WasherFluid',
  EngineOil = 'EngineOil',
  TransmissionFluid = 'TransmissionFluid',
}

export enum FuelTypeEnum {
  Diesel = 'Diesel',
  Unleaded = 'Unleaded',
  AdBlue = 'AdBlue',
}

export enum QuantityTypeEnum {
  ML = 'ML',
  Litres = 'Litres',
}

export enum RecordActivityTypeEnum {
  JumpStartVehicle = 'JumpStartVehicle',
  TowAVehicle = 'TowAVehicle',
}

export enum BatteryHVSOCUnitEnum {
  KWh = 'KWh',
  Percent = 'Percent',
}

export enum Battery12VLevelUnitEnum {
  Voltage = 'Voltage',
  Percent = 'Percent',
}

export enum EngineTypeEnum {
  ICE = 'ICE',
  EV = 'EV',
  Hybrid = 'Hybrid',
}

export enum YesNoEnum {
  Yes = 'Yes',
  No = 'No',
}

export enum YesNoNAEnum {
  Yes = 'Yes',
  No = 'No',
  NA = 'NA',
}

export enum VehicleWashTypeEnum {
  StorageWash = 'StorageWash',
  AutomaticWash = 'AutomaticWash',
  AcidWash = 'AcidWash',
}

export enum TyreActionTypeEnum {
  NoAction = 'NoAction',
  Inflate = 'Inflate',
  Deflate = 'Deflate',
}

export interface EvChargeModel extends MaintenanceBaseModel {
  inputMode: BatteryHVSOCUnitEnum;
  targetValue: number;
  startValue?: number;
  endValue?: number;
}

export interface Battery12VChargeModel extends MaintenanceBaseModel {
  startUnit: Battery12VLevelUnitEnum;
  startValue?: number;
  endUnit?: Battery12VLevelUnitEnum;
  endValue?: number;
}

export interface WarmUpModel extends MaintenanceBaseModel {
  targetMinutes: number;
}

export interface AddFluidsModel extends MaintenanceBaseModel {
  fluidType: FluidTypeEnum;
  quantityType: QuantityTypeEnum;
  quantity: number;
  reason: string;
}

export interface AddFuelModel extends MaintenanceBaseModel {
  fuelType: FuelTypeEnum;
  quantityType: QuantityTypeEnum;
  quantity: number;
  comment: string;
}

export interface RecordActivityModel extends MaintenanceBaseModel {
  recordActivityType: string;
}

export interface BatteryCheckModel extends MaintenanceBaseModel {
  engineType: EngineTypeEnum;
  transportMode: YesNoEnum;
  battery12VHealth?: YesNoNAEnum;
  battery12VHealthComment?: string;
  battery12VChargeRequired?: YesNoNAEnum;
  battery12VLevelUnit?: Battery12VLevelUnitEnum;
  battery12VLevel?: number;
  batteryHVHealth?: YesNoNAEnum;
  batteryHVHealthComment?: string;
  batteryHVChargeRequired?: YesNoNAEnum;
  batteryHVSOCUnit?: BatteryHVSOCUnitEnum;
  batteryHVSOC?: number;
}

export interface VehicleWashModel extends MaintenanceBaseModel {
  vehicleWashType: VehicleWashTypeEnum;
}

export interface TyrePressureModel extends MaintenanceBaseModel {
  tyrePressureThreshold: number;
  tyrePressureSet: number;
  nsfBar: number;
  nsfAction: TyreActionTypeEnum;
  nsfDone: boolean;
  nsrBar: number;
  nsrAction: TyreActionTypeEnum;
  nsrDone: boolean;
  osfBar: number;
  osfAction: TyreActionTypeEnum;
  osfDone: boolean;
  osrBar: number;
  osrAction: TyreActionTypeEnum;
  osrDone: boolean;
}
